import { isAfter, isEqual } from 'date-fns';
import { areOpenIntervalsOverlapping, isValidOpenInterval, OpenInterval } from './dates';
import { ValidatorFn } from './types';

const ssnValidator = RegExp(/^[0-9]{6}-?[0-9]{4}$/);

export class Validators<TItem> {
  public validateNotEmpty(messageProvider: () => string, valueSelector: (item: TItem) => any): ValidatorFn<TItem> {
    return (item) => {
      const value = valueSelector(item);
      if (!value && value !== 0) {
        return messageProvider();
      }
      return undefined;
    };
  }

  public validateEmail(messageProvider: () => string, valueSelector: (item: TItem) => any): ValidatorFn<TItem> {
    return (item) => {
      const value = valueSelector(item);
      if (!value) return undefined;
      const emailValidator = RegExp(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
      return emailValidator.test(value.toString()) ? undefined : messageProvider();
    };
  }

  public validateSsn(messageProvider: () => string, valueSelector: (item: TItem) => any): ValidatorFn<TItem> {
    return (item) => {
      const value = valueSelector(item);
      if (!value) return undefined;
      return ssnValidator.test(value.toString()) ? undefined : messageProvider();
    };
  }

  public validateAfterStart(
    startLabel: string,
    endLabel: string,
    valueSelector: (item: TItem) => { start?: Date | null; end?: Date | null }
  ): ValidatorFn<TItem> {
    return (item) => {
      const value = valueSelector(item);
      if (!value) return undefined;
      const { start, end } = value;
      if (!start || !end) return undefined;

      if (isAfter(end, start)) return undefined;

      return `${endLabel} skal være efter ${startLabel}`;
    };
  }

  public validateSameOrAfterStart(
    startLabel: string,
    endLabel: string,
    valueSelector: (item: TItem) => { start?: Date | null; end?: Date | null }
  ): ValidatorFn<TItem> {
    return (item) => {
      const value = valueSelector(item);
      if (!value) return undefined;
      const { start, end } = value;
      if (!start || !end) return undefined;

      if (isEqual(end, start) || isAfter(end, start)) return undefined;

      return `${endLabel} skal være samme eller efter ${startLabel}`;
    };
  }

  public validateNoOverlappingIntervals(
    valueSelector: (item: TItem) => {
      interval?: Partial<OpenInterval>;
      compareToIntervals?: Partial<OpenInterval>[];
    }
  ): ValidatorFn<TItem> {
    return (item) => {
      const { interval, compareToIntervals } = valueSelector(item) ?? {};

      if (!isValidOpenInterval(interval)) {
        return undefined;
      }

      const anyValidOverlappingIntervals = compareToIntervals?.some((innerInterval) => {
        const validAndOverlapping = isValidOpenInterval(innerInterval) && areOpenIntervalsOverlapping(interval, innerInterval, { inclusive: true });
        return validAndOverlapping;
      });

      if (anyValidOverlappingIntervals) {
        return `Overlap med andre intervaller ikke tilladt`;
      }
      return undefined;
    };
  }
}
